import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { company, DEFAULT_COMPANIES } from '~/company/Company';
import { CompanyName } from '~/company/interface';
import SkeletonListRows from '~/components/Skeleton/SkeletonList';
import { useGlobal } from '~/hooks/useGlobal';

import { orderStore } from '~/stores';

import MainSidebar from '../../components/MainSidebar/MainSidebar';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';

import MobileHeaderLayout from '../Main/MobileHeader/MobileHeaderLayout';
import MobileHeaderTitle from '../Main/MobileHeader/MobileHeaderTitle';

import EmptyOrders from './EmptyOrders';
import { useOrders } from './hooks';
import { OrdersTab } from './interfaces';
import OrderItem from './OrderItem';
import OrderItemSkeleton from './OrderItemSkeleton';
import styles from './Orders.module.scss';

const Orders = () => {
  const { t } = useTranslation();
  const { isMobile } = useGlobal();
  const navigate = useNavigate();
  const refLoaderTrigger = useRef<HTMLDivElement>(null);
  const { isObservableElementVisible, stopObserver, runObserver } =
    useIntersectionObserver();

  const noTabs = DEFAULT_COMPANIES.includes(company.name) && !isMobile;
  const { tab = OrdersTab.ACTIVE } = useParams<{ tab?: OrdersTab }>();

  const { isLoading, orderList, isPageLoading, activeOrders, finishedOrders } =
    useOrders({
      stopObserver,
      runObserver: () => {
        stopObserver();
        runObserver(refLoaderTrigger);
      },
      tab: noTabs ? undefined : tab,
    });

  useEffect(() => {
    if (
      isLoading ||
      !isObservableElementVisible ||
      !orderStore.downloadIsPossible
    ) {
      /**
       * No need to do something if:
       * - orders is loading
       * - user did not scroll down to "fetch more" hidden component
       * - if downloading new items is denied, it could be in case when user achieved end of list
       * */
      return;
    }

    // Check if there are orders on the current tab
    const currentTabOrders =
      tab === OrdersTab.ACTIVE ? activeOrders : finishedOrders;
    const isCurrentTabEmpty = currentTabOrders.length === 0;

    // If there are no orders on the current tab and this is not noTabs mode, do not load more
    if (!noTabs && isCurrentTabEmpty) {
      return;
    }

    orderStore.setCurrentOrdersPage(orderStore.currentOrdersPage + 1);
  }, [isObservableElementVisible, tab, noTabs]);

  const renderActiveOrders = () => {
    return activeOrders.length > 0 ? (
      <section className="order-history-items">
        <div className="order-items">
          {activeOrders.map((order) => (
            <OrderItem order={order} key={order.id} isActive={true} />
          ))}
        </div>
      </section>
    ) : (
      <EmptyOrders orderType={t('active')} />
    );
  };

  const renderOrdersHistory = () => {
    return finishedOrders.length > 0 ? (
      <section className="order-history-items">
        <div className="order-items">
          {finishedOrders.map((order) => (
            <OrderItem order={order} key={order.id} />
          ))}
        </div>
      </section>
    ) : (
      <EmptyOrders orderType={t('completed')} />
    );
  };

  const getPageContent = () => {
    if (isLoading) {
      return (
        <section className="order-history-items">
          <div className="order-items">
            <SkeletonListRows rows={8}>
              <OrderItemSkeleton />
            </SkeletonListRows>
          </div>
        </section>
      );
    }

    if (noTabs) {
      return (
        <>
          {renderActiveOrders()}
          <div className="history-title">{t('orderHistory')}</div>
          {renderOrdersHistory()}
        </>
      );
    }

    if (!orderList.length) {
      return (
        <EmptyOrders
          orderType={t(tab === OrdersTab.ACTIVE ? 'active' : 'completed')}
        />
      );
    }
    return (
      <section className="order-history-items">
        <div className="order-items">
          {orderList.map((order) => (
            <OrderItem
              order={order}
              key={order.id}
              isActive={tab === OrdersTab.ACTIVE}
            />
          ))}
        </div>
      </section>
    );
  };
  const onBackClick = () => {
    navigate(
      company.match({
        default: '/cabinet',
      }),
    );
  };
  return (
    <>
      <MobileHeaderLayout
        content={
          <MobileHeaderTitle text={t('orders')} onBackClick={onBackClick} />
        }
      />
      <div className="content orders" data-company={company.name}>
        <aside className="sidebar">
          <MainSidebar />
        </aside>

        <main
          className={classes(['container', styles.container])}
          data-company={company.name}
        >
          <h1 className="page-title">
            {t('orders')} <Counter value={activeOrders.length} />
          </h1>
          {!noTabs && (
            <section className="filters">
              <div className="right-block">
                <div className="years-wrapper">
                  <Link
                    to="/cabinet/orders"
                    replace
                    className={classes([
                      'year',
                      tab === OrdersTab.ACTIVE && 'current',
                    ])}
                  >
                    {t('activeOrders')}
                  </Link>
                  <Link
                    to="/cabinet/orders/history"
                    replace
                    className={classes([
                      'year',
                      tab === OrdersTab.HISTORY && 'current',
                    ])}
                  >
                    {t('orderHistory')}
                  </Link>
                </div>
              </div>
            </section>
          )}
          {getPageContent()}
          <div style={{ height: 10 }} ref={refLoaderTrigger} />
          {isPageLoading && (
            <div className={styles.loadingSpinner}>
              <span className="spinner" />
            </div>
          )}
        </main>
      </div>
    </>
  );
};

const Counter = ({ value }: { value: string | number }) => {
  if (company.name === CompanyName.CityDrinks) {
    return null;
  }

  return <span className="count">{value}</span>;
};

export default observer(Orders);
